import {
  MdMoreHoriz,
  MdPrint,
  MdFileOpen,
  MdStorefront,
  MdOutlineCategory,
  MdOutlineCalendarMonth,
  MdAttachMoney,
  MdQueryStats,
  MdOutlineSwapVert,
  MdOutlineWorkHistory,
  MdOutlineFileDownload,
  MdOutlineInventory,
  MdFilterAlt,
} from "react-icons/md";
import { AnimatePresence } from "framer-motion";
import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useEffect, useMemo, useState, useTransition } from "react";
import { useParams } from "react-router-dom";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import clsx from "clsx";
import userPreferences from "../../store/userPreferences";
import { REPORTS_TABS } from "../../helpers/constants";
import LinksList from "../../components/LinksList/LinksList";
import { motion } from "framer-motion";

import { useTranslation } from "react-i18next";
import ReportByCategories from "../../components/Reports/ReportByCategories";
import ReportByMonth from "../../components/Reports/ReportByMonth";
import ReportByPaymentMethods from "../../components/Reports/ReportByPaymentMethods";
import ReportByClientProvider from "../../components/Reports/ReportByClientProvider";
import { BUTTON_STYLES, ICON_SIZES } from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import TodayTotalsBar from "../../components/TodayTotalsBar/TodayTotalsBar";
import useMainFilter from "../../hooks/useMainFilter";
import useAccountsDropdownMenu from "../../hooks/useAccountsDropdownMenu";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import ReportsSidebar from "./ReportsSidebar";
import ReportLastSevenDays from "../../components/Reports/ReportLastSevenDays";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import CurrentAccountCard from "../../components/CurrentAccountCard/CurrentAccountCard";
import AccountNotFound from "../../components/AccountNotFound/AccountNotFound";
import PageSubtitleAccount from "../../components/PageTitle/PageSubtitleAccount";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import ActiveMainFilterMessage from "../../components/ActiveMainFilterMessage/ActiveMainFilterMessage";
import useCurrentUser from "../../hooks/useCurrentUser";
import SelectionTotals from "../../components/SelectionTotals/SelectionTotals";
import {
  exportToCsv,
  exportToExcel,
  exportToJson,
  getChartSelectionToTransactionsFormat,
  showNotification,
} from "../../helpers/utils";
import useCollapsibles from "../../hooks/useCollapsibles";
import IncomeExpensesPerYear from "../../components/IncomeExpensesPerYear/IncomeExpensesPerYear";
import ReportsYearSelector from "../../components/ReportsYearSelector/ReportsYearSelector";
import useModalsStore from "src/store/modalsStore";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import useAllTransactions from "src/hooks/useAllTransactions";
import ExportTransactionsModal, {
  EXPORT_TYPE,
  FILE_TYPES,
} from "src/components/ExportTransactionsModal/ExportTransactionsModal";
import { get, map } from "lodash";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import useDarkMode from "src/hooks/useDarkMode";
import ReportByStatus from "src/components/Reports/ReportByStatus";
import MarqueeRow from "src/components/MarqueeRow/MarqueeRow";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

const Reports: React.FC = () => {
  const [isPending, startTransition] = useTransition();

  const { isEnabled: isDarkMode } = useDarkMode();
  const [canScroll, setCanScroll] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [isExportingData, setIsExportingData] = useState<boolean>(false);
  const { isLoadingCustomizedCategories, isLoadingCustomizedSources } =
    useAllTransactions();
  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
  const { isLoadingAccounts } = useCurrentUser();
  const { collapsiblePreferences } = useCollapsibles();
  const accountsGlobalMenu = useAccountsDropdownMenu({ linkTo: "reports" });
  const { isLoadingCurrentAccount, currentAccountData, currencySymbol } =
    useCurrentAccount();
  const { isFilteringTransactions } = useFilteredTransactions();
  const { reportsActiveTab, setReportsActiveTab } = userPreferences();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const [tableData, setTableData] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState<number>(reportsActiveTab);
  const { isMobile } = useMobile();
  const { setShowSearchModal } = useModalsStore();

  const { mainFilter, setMainFilter, isMainFilterActive } = useMainFilter();

  const { account: accountParameter } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    startTransition(() => setActiveTab(activeTab));
    startTransition(() => setReportsActiveTab(activeTab));
    startTransition(() => setMainFilter({ account: accountParameter }));
    startTransition(() => setSelectedRows([]));
  }, [activeTab, accountParameter]);

  const isLoadingSomething = useMemo(
    () =>
      [
        !!isLoadingCurrentAccount,
        !!isFilteringTransactions,
        !!isLoadingAccounts,
        !!isLoadingCustomizedCategories,
        !!isLoadingCustomizedCategories,
      ]?.includes(true),
    [
      isLoadingCurrentAccount,
      isFilteringTransactions,
      isLoadingAccounts,
      isLoadingCustomizedCategories,
      isLoadingCustomizedSources,
    ]
  );

  const shouldHideRightSidebar = useMemo(() => {
    return (
      isLoadingSomething ||
      !mainFilter?.account ||
      !currentAccountData ||
      currentAccountData?.settings?.disabled
    );
  }, [isLoadingSomething, mainFilter?.account, currentAccountData]);

  const selection = useMemo(() => {
    if (selectedRows?.length) {
      return getChartSelectionToTransactionsFormat({
        data: selectedRows,
      });
    }

    return [];
  }, [selectedRows]);

  const getDataToExport = ({
    exportType,
    includeCurrencySymbol,
  }: {
    exportType: Partial<EXPORT_TYPE>;
    includeCurrencySymbol: boolean;
  }) => {
    switch (activeTab) {
      case REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            Fecha: get(item, "date", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }
      case REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            Mes: Number(get(item, "month", "")) + 1,
            Nombre: get(item, "monthFormatted", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }
      case REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            Categoría: get(item, "category", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }
      case REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            "Origen / Destino": get(item, "payTo", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }
      case REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            "Método de pago": get(item, "paymentMethod", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }

      case REPORTS_TABS.TRANSACTIONS_BY_STATUS: {
        const data = exportType === EXPORT_TYPE.ALL ? tableData : selectedRows;

        return map(data, (item) => {
          return {
            Estado: get(item, "statusFormatted", ""),
            Ingresos: get(
              item,
              includeCurrencySymbol ? "incomeFormatted" : "income",
              0
            ),
            Gastos: get(
              item,
              includeCurrencySymbol ? "expensesFormatted" : "expenses",
              0
            ),
            Diferencia: get(
              item,
              includeCurrencySymbol ? "differenceFormatted" : "difference",
              0
            ),
          };
        });
      }

      default: {
        return [];
      }
    }
  };

  const exportData = ({
    includeCurrencySymbol,
    formatNumbers,
    fileName,
    fileFormat,
    exportType,
  }: any) => {
    setIsExportingData(false);

    const data = getDataToExport({ exportType, includeCurrencySymbol });

    try {
      if (fileFormat === FILE_TYPES.XLSX) {
        exportToExcel({ data, fileName });
      } else if (fileFormat === FILE_TYPES.CSV) {
        exportToCsv({ data, fileName });
      } else if (fileFormat === FILE_TYPES.JSON) {
        exportToJson({ data, fileName });
      }

      setShowExportDialog(false);
      showNotification({
        kind: "success",
        message: "Datos exportados con éxito",
      });
    } catch (e) {
      showNotification({
        kind: "error",
        message: "Ocurrió un error al exportar los datos",
      });
    } finally {
      setIsExportingData(false);
    }
  };

  const mobileMenuMarquee = useMemo(() => {
    switch (reportsActiveTab) {
      case REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS: {
        return "Últimas transacciones realizadas";
      }
      case REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART: {
        return "Transacciones mensuales";
      }
      case REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART: {
        return "Transacciones por categoría";
      }
      case REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART: {
        return "Transacciones por Origen / Destino";
      }
      case REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART: {
        return "Transacciones por método de pago";
      }
      case REPORTS_TABS.TRANSACTIONS_BY_STATUS: {
        return "Transacciones por estado";
      }
      default: {
        return null;
      }
    }
  }, [reportsActiveTab]);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          shouldHideRightSidebar ? null : isMobile ? null : (
            <ReportsSidebar
              append={<SelectionTotals transactions={selection} isSummarized />}
            />
          )
        }
        mobileMenuChildren={<MarqueeRow text={mobileMenuMarquee} />}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {mainFilter?.account ? (
              <>
                {isLoadingSomething ? (
                  <LoadingWithTextPage />
                ) : (
                  <>
                    {currentAccountData &&
                    !currentAccountData?.settings?.disabled ? (
                      <>
                        <PageTitle
                          icon={<MdQueryStats size={ICON_SIZES.MD} />}
                          title={t("shared.reports")}
                          subtitle={
                            isMobile && mainFilter?.account ? (
                              <PageSubtitleAccount {...currentAccountData} />
                            ) : null
                          }
                          rightSide={
                            <>
                              <ButtonGroup
                                buttons={[
                                  {
                                    icon: (
                                      <MdOutlineFileDownload
                                        size={ICON_SIZES.MD}
                                      />
                                    ),
                                    tooltip: t("Exportar"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                    onClick: () => {
                                      setShowExportDialog(true);
                                    },
                                  },
                                  {
                                    icon: isMainFilterActive ? (
                                      <MdFilterAlt
                                        className="text-red-500 dark:text-purple-500"
                                        size={ICON_SIZES.MD}
                                      />
                                    ) : (
                                      <MdFilterAlt size={ICON_SIZES.MD} />
                                    ),
                                    onClick: () => {
                                      setShowSearchModal(true);
                                    },
                                    tooltip: isMobile
                                      ? ""
                                      : t("transactions_search.title"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                  },
                                  accountsGlobalMenu,
                                  {
                                    icon: <MdMoreHoriz size={ICON_SIZES.MD} />,
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                    hide: true,
                                    options: [
                                      {
                                        text: "Imprimir",
                                        icon: <MdPrint size={ICON_SIZES.MD} />,
                                        onClick: () => {},
                                      },
                                      {
                                        text: t("Exportar"),
                                        onClick: () => {
                                          setShowExportDialog(true);
                                        },
                                        icon: (
                                          <MdOutlineFileDownload
                                            size={ICON_SIZES.MD}
                                          />
                                        ),
                                        disabled: !tableData?.length,
                                      },
                                      {
                                        text: "Exportar en CSV",
                                        icon: (
                                          <MdFileOpen size={ICON_SIZES.MD} />
                                        ),
                                        onClick: () => {},
                                      },
                                    ],
                                  },
                                  generalOptionsDropdown,
                                ]}
                                gap={1}
                              />
                            </>
                          }
                        />
                        <TodayTotalsBar />

                        <SlideDownUpAnimation
                          show={
                            !!(isMobile && collapsiblePreferences.accountOpen)
                          }
                        >
                          <CurrentAccountCard />
                        </SlideDownUpAnimation>

                        <div
                          className={clsx([
                            "flex",
                            "flex-col",
                            "gap-4",
                            "w-full",
                          ])}
                          id="focused-section"
                        >
                          <LinksList
                            links={[
                              {
                                text: t("Últimas transacciones"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS,
                                icon: (
                                  <MdOutlineWorkHistory
                                    size={ICON_SIZES.MD_LG}
                                  />
                                ),
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS &&
                                  isPending,
                              },
                              {
                                text: t("shared.transaction_type"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_BY_TYPE_CHART
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_BY_TYPE_CHART,
                                icon: (
                                  <MdOutlineSwapVert size={ICON_SIZES.MD_LG} />
                                ),
                                hide: true,
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_BY_TYPE_CHART &&
                                  isPending,
                              },
                              {
                                text: t("shared.monthly"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART,
                                icon: (
                                  <MdOutlineCalendarMonth
                                    size={ICON_SIZES.MD_LG}
                                  />
                                ),
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART &&
                                  isPending,
                              },
                              {
                                text: t("shared.categories"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART,
                                icon: (
                                  <MdOutlineCategory size={ICON_SIZES.MD_LG} />
                                ),
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART &&
                                  isPending,
                              },
                              {
                                text: t("Origen / Destino"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART,
                                icon: <MdStorefront size={ICON_SIZES.MD_LG} />,
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART &&
                                  isPending,
                              },
                              {
                                text: t("shared.payment_methods"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART,
                                icon: <MdAttachMoney size={ICON_SIZES.MD_LG} />,
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART &&
                                  isPending,
                              },
                              {
                                text: t("Estado"),
                                onClick: () =>
                                  setActiveTab(
                                    REPORTS_TABS.TRANSACTIONS_BY_STATUS
                                  ),
                                active:
                                  activeTab ===
                                  REPORTS_TABS.TRANSACTIONS_BY_STATUS,
                                icon: (
                                  <MdOutlineInventory size={ICON_SIZES.MD_LG} />
                                ),
                                isLoading:
                                  activeTab ===
                                    REPORTS_TABS.TRANSACTIONS_BY_STATUS &&
                                  isPending,
                              },
                            ]}
                          />

                          <ActiveMainFilterMessage
                            onShowSearch={() => setShowSearchModal(true)}
                          />

                          <ReportsYearSelector />

                          <IncomeExpensesPerYear
                            filter={
                              activeTab ===
                              REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS
                                ? "last-seven-days"
                                : "filtered-transactions"
                            }
                          />

                          <motion.div
                            className={clsx([
                              "w-full",
                              "flex",
                              "flex-col",
                              "gap-4",
                            ])}
                            key={activeTab}
                            {...(isMobile
                              ? {
                                  initial: {
                                    opacity: 0.99,
                                  },
                                  animate: { opacity: 1 },
                                  transition: { duration: 0.1 },
                                }
                              : {
                                  initial: { opacity: 0 },
                                  animate: { opacity: 1 },
                                  exit: { opacity: 0 },
                                  transition: { duration: 2, ease: "easeOut" },
                                })}
                            onAnimationComplete={() => {
                              if (isMobile) {
                                if (!!collapsiblePreferences.accountOpen) {
                                  setCanScroll(true);
                                } else {
                                  setCanScroll(
                                    collapsiblePreferences.statsTableOpen ||
                                      collapsiblePreferences.statsChartOpen
                                  );
                                }
                              }
                            }}
                          >
                            {/* {isPending ? (
                              <LoadingWithTextPage />
                            ) : ( */}
                            <>
                              {!isMobile && (
                                <MarqueeRow text={mobileMenuMarquee} />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_LAST_SEVEN_DAYS && (
                                <ReportLastSevenDays
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_PER_MONTH_CHART && (
                                <ReportByMonth
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_BY_CATEGORY_CHART && (
                                <ReportByCategories
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_PER_CLIENT_PROVIDER_CHART && (
                                <ReportByClientProvider
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_BY_PAYMENT_METHOD_CHART && (
                                <ReportByPaymentMethods
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}

                              {activeTab ===
                                REPORTS_TABS.TRANSACTIONS_BY_STATUS && (
                                <ReportByStatus
                                  onSelectionChange={setSelectedRows}
                                  onDataChange={(data: any[]) => {
                                    setTableData(data);
                                  }}
                                />
                              )}
                            </>
                            {/* )} */}

                            {isMobile && (
                              <SelectionTotals
                                transactions={selection}
                                isSummarized
                              />
                            )}
                          </motion.div>
                        </div>
                        <ScrollToSection
                          targetId="focused-section"
                          enabled={canScroll}
                        />
                      </>
                    ) : (
                      <AccountNotFound
                        linkTo="reports"
                        exists={!!currentAccountData}
                        disabled={!!currentAccountData?.settings?.disabled}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountNotFound linkTo="reports" />
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>
      {showExportDialog && (
        <ExportTransactionsModal
          onClose={() => setShowExportDialog(false)}
          onConfirm={async (data: any) => {
            exportData(data);
          }}
          isLoading={isExportingData}
          selectedRows={selectedRows?.length}
          totalRows={
            getDataToExport({
              exportType: EXPORT_TYPE.ALL,
              includeCurrencySymbol: false,
            })?.length
          }
        />
      )}
    </>
  );
};

export default Reports;
